import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  Select,
  Tag,
  TimePicker,
} from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";
import { useEffect, useState } from "react";
import useDownloadEmployeeXlsx from "../../../hooks/useDownloadExcelWithApi";
import useModal from "../../../hooks/useModal";
import {
  ExportStatementType,
  GetSupplierPaymentHistoryParams,
  TransactionsDetails,
} from "../../../types/statements";
import useTable from "../../../hooks/useTable";
import {
  addKeyInArray,
  customTimeConverter,
  formatSouthAsianNumber,
} from "../../../utils/utils";
import { useGetPaymentHistoryQuery } from "../../../features/supplierPaymentHistory/supplierPaymentHistoryApi";
import useError from "../../../hooks/useError";
import { capitalize } from "lodash";
import { API_URL, dbDateFormat } from "../../../constants/constants";
import { disableFutureDates } from "../../../lib/time";
import { BASE_API } from "../../../constants/apiUrls";
import IconButton from "../../../components/ui/IconButton";
import BottomDropdownIcon from "../../../assets/icons/BottomDropdownIcon";
import StatementDetailsModal from "../../../components/statements/StatementDetailsModal";
import AppLayout from "../../../layouts/AppLayout";
import FilterIcon from "../../../assets/icons/FilterIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import CustomTable from "../../../components/ui/CustomTable";
import { useGetTradersSummeryQuery } from "../../../features/trader/traderApi";
import { GetTradersSummeryResponseType } from "../../../types/trader.type";
import emptyUser from "../../../assets/images/empty_user.jpg";

type TimeFormat = {
  formTime?: string;
  toTime?: string;
};

export default function Statement() {
  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();
  const [time, setTime] = useState<TimeFormat>();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("dealers");
  const { open, handleOpen, handleClose } = useModal<
    TransactionsDetails | undefined
  >();
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetSupplierPaymentHistoryParams>();
  const endTime = time?.toTime
    ? customTimeConverter(dayjs(time?.toTime, "HH:mm:ss").format("hh:mm:ss A"))
    : undefined;
  const {
    data: paymentHistoryData,
    isLoading,
    isError,
    error,
    isFetching,
  } = useGetPaymentHistoryQuery({
    ...filter,
    search: keyword,
    startDate: filter?.startDate
      ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
      : undefined,
    endDate: filter?.endDate
      ? `${filter?.endDate} ${endTime || "23:59:59"}`
      : undefined,
  });
  const {
    data: treaderGraphData,
    isLoading: traderGraphLoading,
    isError: isTraderGraphError,
    error: traderGraphError,
  } = useGetTradersSummeryQuery({
    ...filter,
    search: keyword,
    startDate: filter?.startDate
      ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
      : undefined,
    endDate: filter?.endDate
      ? `${filter?.endDate} ${endTime || "23:59:59"}`
      : undefined,
  });
  useError(isError, error, "Get statement data fail");
  useError(isTraderGraphError, traderGraphError, "Get trader graph data fail");

  const columns: ColumnsType<DataType> = [
    {
      title: "Zone",
      dataIndex: "merchant",
      align: "center",
      render: (val) => {
        return val ? val?.zone : "N/A";
      },
    },
    {
      title: "District",
      dataIndex: "merchant",
      align: "center",
      render: (val) => {
        return val ? val?.district : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Dealer Code</div>,
      key: "dealerCode",
      align: "center",
      render: (val) => {
        return val?.merchant?.dealerNo || "-";
      },
    },
    {
      title: <div style={{ width: "200px" }}>Dealer Name</div>,
      key: "dealerName",
      render: (val) => {
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 rounded-full">
              <img
                src={val?.merchant?.profile?.logoImage}
                alt="Dealer"
                className="rounded-full w-full h-full object-cover"
              />
            </div>

            <span className="flex-1">{val?.merchant?.name}</span>
          </div>
        );
      },
    },
    {
      title: "Date",
      key: "date",
      align: "center",
      render: (val) => {
        return (
          <span className="text-nowrap">
            {val?.createdAt ? moment(val?.createdAt).format("DD/MM/YYYY") : "-"}
          </span>
        );
      },
    },
    {
      title: "Time",
      align: "center",
      key: "time",
      render: (val) => {
        return (
          <span className="text-nowrap">
            {val?.createdAt ? moment(val?.createdAt).format("hh:mm A") : "-"}
          </span>
        );
      },
    },
    {
      title: <div className="text-nowrap">Payment by</div>,
      dataIndex: "paymentBy",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Business Unit</div>,
      key: "businessUnit",
      render: (val) => val?.businessUnit?.name,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      align: "center",
      key: "amount",
      render: (val) => formatSouthAsianNumber(val),
    },
    {
      title: <div className="text-nowrap">Payment Status</div>,
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={status === "success" ? "success" : "warning"}
          className="rounded-full"
          bordered={false}
        >
          {status === "success" ? "Success" : "Pending"}
        </Tag>
      ),
    },
    // {
    //   title: <span className="text-nowrap">Bank Status</span>,
    //   dataIndex: "mStatus",
    //   key: "bankStatus",
    //   render: (status) => (
    //     <Tag
    //       color={status === "success" ? "success" : "warning"}
    //       className={"rounded-full"}
    //       bordered={false}
    //     >
    //       {status === "success" ? "Success" : "Pending"}
    //     </Tag>
    //   ),
    // },
    {
      title: <span className="text-nowrap">MR Status</span>,
      dataIndex: "omrStatus",
      key: "omrStatus",
      render: (status) => (
        <Tag
          color={
            status === "success" || status === "approved"
              ? "success"
              : "warning"
          }
          className="rounded-full"
          bordered={false}
        >
          {capitalize(status) || "Pending"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (val) => (
        <Button
          type="default"
          className="hover:!border-slate-200"
          onClick={() => handleOpen({ type: "VIEW", data: val })}
        >
          Details
        </Button>
      ),
    },
  ];

  // Trader columns
  const traderColumns: ColumnsType<GetTradersSummeryResponseType> = [
    {
      title: "Zone",
      dataIndex: "suppliers",
      align: "center",
      render: (val) => {
        return val ? val?.zone : "N/A";
      },
    },
    {
      title: "District",
      dataIndex: "suppliers",
      align: "center",
      render: (val) => {
        return val ? val?.district : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Dealer Code</div>,
      key: "suppliers",
      align: "center",
      dataIndex: "suppliers",
      render: (val) => {
        return val?.dealerCode || "-";
      },
    },
    {
      title: <div style={{ width: "200px" }}>Dealer Name</div>,
      key: "dealerName",
      dataIndex: "suppliers",
      render: (val) => {
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 rounded-full">
              <img
                src={val?.profile?.logoImage || emptyUser}
                alt="Dealer"
                className="rounded-full w-full h-full object-cover"
              />
            </div>

            <span className="flex-1">{val?.name}</span>
          </div>
        );
      },
    },
    {
      title: <div style={{ width: "200px" }}>Trader Name</div>,
      key: "dealerName",
      render: (val) => {
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 rounded-full">
              <img
                src={val?.profile?.logoImage}
                alt="Dealer"
                className="rounded-full w-full h-full object-cover"
              />
            </div>

            <span className="flex-1">{val?.profile?.merchantName}</span>
          </div>
        );
      },
    },
    {
      title: "Date",
      key: "date",
      align: "center",
      render: (val) => {
        return (
          <span className="text-nowrap">
            {val?.createdAt ? moment(val?.createdAt).format("DD/MM/YYYY") : "-"}
          </span>
        );
      },
    },
    {
      title: "Time",
      align: "center",
      key: "time",
      render: (val) => {
        return (
          <span className="text-nowrap">
            {val?.createdAt ? moment(val?.createdAt).format("hh:mm A") : "-"}
          </span>
        );
      },
    },
    {
      title: <div className="text-nowrap">Payment by</div>,
      dataIndex: "paymentBy",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Business Unit</div>,
      key: "businessUnit",
      dataIndex: "businessUnit",
      render: (val) => {
        return val ? val?.name : "N/A";
      },
    },
    {
      title: "Amount",
      dataIndex: "tradersToSupplierTransactionAmount",
      align: "center",
      key: "amount",
      render: (val) => formatSouthAsianNumber(val),
    },
    // {
    //   title: <div className="text-nowrap">Payment Status</div>,
    //   dataIndex: "status",
    //   key: "status",
    //   render: (status) => (
    //     <Tag
    //       color={status === "success" ? "success" : "warning"}
    //       className="rounded-full"
    //       bordered={false}
    //     >
    //       {status === "success" ? "Success" : "Pending"}
    //     </Tag>
    //   ),
    // },
    // {
    //   title: <span className="text-nowrap">Bank Status</span>,
    //   dataIndex: "mStatus",
    //   key: "bankStatus",
    //   render: (status) => (
    //     <Tag
    //       color={status === "success" ? "success" : "warning"}
    //       className={"rounded-full"}
    //       bordered={false}
    //     >
    //       {status === "success" ? "Success" : "Pending"}
    //     </Tag>
    //   ),
    // },
    // {
    //   title: <span className="text-nowrap">MR Status</span>,
    //   dataIndex: "omrStatus",
    //   key: "omrStatus",
    //   render: (status) => (
    //     <Tag
    //       color={
    //         status === "success" || status === "approved"
    //           ? "success"
    //           : "warning"
    //       }
    //       className="rounded-full"
    //       bordered={false}
    //     >
    //       {capitalize(status) || "Pending"}
    //     </Tag>
    //   ),
    // },
    {
      title: "Action",
      key: "action",
      render: (val) => {
        console.log("Action column val:", val);
        return (
          <Button
            type="default"
            className="hover:!border-slate-200"
            onClick={() => handleOpen({ type: "VIEW", data: val })}
          >
            Details
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const handleStatementCancel = () => {
    handleFilter("startDate", undefined);
    handleFilter("endDate", undefined);
    setTime(undefined);
  };

  // useEffect(() => {
  //   handleFilter("businessUnit", 3);
  // }, [handleFilter]);

  const handleSelectChange = (value: any) => {
    setSelectedCategory(value);
  };

  const DateTimeSelector = (
    <div className="p-6 border rounded-lg shadow-md max-w-md mx-auto bg-white clear-button-hide">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-600 mb-1">From Date</label>
          <DatePicker
            value={
              filter?.startDate ? dayjs(filter?.startDate, dbDateFormat) : null
            }
            onChange={(e: Dayjs | null) =>
              handleFilter("startDate", dayjs(e).format(dbDateFormat))
            }
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.formTime ? dayjs(time?.formTime, "HH:mm:ss") : null}
            onChange={(e: Dayjs | null) =>
              setTime((prev) => ({
                ...prev,
                formTime: dayjs(e).format("HH:mm:ss"),
              }))
            }
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">To Date</label>
          <DatePicker
            value={
              filter?.endDate ? dayjs(filter?.endDate, dbDateFormat) : null
            }
            onChange={(e: Dayjs | null) =>
              handleFilter("endDate", dayjs(e).format(dbDateFormat))
            }
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.toTime ? dayjs(time?.toTime, "HH:mm:ss") : null}
            onChange={(e: Dayjs | null) => {
              setTime((prev) => ({
                ...prev,
                toTime: dayjs(e).format("HH:mm:ss"),
              }));
            }}
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>
      </div>
      <div className="flex justify-end mt-6">
        <Button className="text-gray-600" onClick={handleStatementCancel}>
          Cancel
        </Button>
        {/* <Button
          type="primary"
          className="!text-primary !bg-[#147AFF33]"
          onClick={() => setOpenDropdown(false)}
        >
          View Statement
        </Button> */}
      </div>
    </div>
  );

  const { page: fPage, limit: fLimit, ...filterData } = filter;

  const handleDownload = () => {
    downloadXlsx(
      API_URL + BASE_API.exportStatement,
      {
        ...filterData,
        search: keyword,
        startDate: filter?.startDate
          ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
          : undefined,
        endDate: filter?.endDate
          ? `${filter?.endDate} ${endTime || "23:59:59"}`
          : undefined,
        skip: 0,
        limit: 100000000000,
      },
      `sPay Statement_dealers${moment().format(
        "DD MMMM YYYY"
      )}_${moment().format("h.mm.ss A")}`,
      "transactions",
      (data: ExportStatementType["data"]["transactions"][0]) => {
        return {
          "Dealer Code": data?.merchant?.dealerNo ?? "-",
          "Dealer Name": data?.merchant?.name ?? "-",
          "Transfer Date": data?.createdAt
            ? moment(data?.createdAt).utc().format("DD/MM/YYYY")
            : "-",
          "Transfer Time": data?.createdAt
            ? moment(data?.createdAt).utc().format("hh:mm A")
            : "-",
          "Transaction Id": data?.transactionId ?? "-",
          "Business Unit": data?.businessUnit?.name ?? "-",
          Amount: Number(data?.amount || 0),
          "Bank Account Name": data?.businessUnit?.bankAccountName ?? "-",
          "Bank Account Number": data?.businessUnit?.bankAccountNo ?? "-",
          "Payment Status":
            capitalize(data?.status === "success" ? "Success" : "pending") ??
            "Pending",
          "MR Id": data?.omrId ?? "-",
          "MR Status":
            capitalize(
              data?.omrStatus === "success"
                ? "Success"
                : data?.omrStatus === "approved"
                ? "Approved"
                : "pending"
            ) ?? "Pending",
          "Bank Status":
            capitalize(data?.mStatus === "success" ? "Success" : "pending") ??
            "Pending",
        };
      }
    );
  };

  const handleDownloadTraders = () => {
    downloadXlsx(
      API_URL + BASE_API.getTradersSummery,
      {
        ...filterData,
        search: keyword,
        startDate: filter?.startDate
          ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
          : undefined,
        endDate: filter?.endDate
          ? `${filter?.endDate} ${endTime || "23:59:59"}`
          : undefined,
        skip: 0,
        limit: 100000000000,
      },
      `sPay Statement_traders${moment().format(
        "DD MMMM YYYY"
      )}_${moment().format("h.mm.ss A")}`,
      "rows",
      (data: GetTradersSummeryResponseType["data"]["rows"][0]) => {
        return {
          "Dealer Code": data?.suppliers?.dealerCode ?? "-",
          "Dealer Name": data?.profile?.merchantName ?? "-",
          "Transfer Date": data?.createdAt
            ? moment(data?.createdAt).utc().format("DD/MM/YYYY")
            : "-",
          "Transfer Time": data?.createdAt
            ? moment(data?.createdAt).utc().format("hh:mm A")
            : "-",
          // "Transaction Id": data?.transactionId ?? "-",
          // "Business Unit": data?.businessUnit?.name ?? "-",
          Amount: Number(data?.tradersToSupplierTransactionAmount || 0),
          // "Bank Account Name": data?.businessUnit?.bankAccountName ?? "-",
          // "Bank Account Number": data?.businessUnit?.bankAccountNo ?? "-",
          "Payment Status":
            capitalize(data?.status === "success" ? "Success" : "pending") ??
            "Pending",
          // "MR Id": data?.omrId ?? "-",
          // "MR Status":
          //   capitalize(
          //     data?.omrStatus === "success"
          //       ? "Success"
          //       : data?.omrStatus === "approved"
          //       ? "Approved"
          //       : "pending"
          //   ) ?? "Pending",
          // "Bank Status":
          //   capitalize(data?.mStatus === "success" ? "Success" : "pending") ??
          //   "Pending",
        };
      }
    );
  };

  return (
    <AppLayout title="Statement">
      {open.type === "VIEW" && (
        <StatementDetailsModal
          open={open.open}
          type={open.type}
          data={open.data}
          selectedCategory={selectedCategory}
          handleClose={handleClose}
        />
      )}

      <div>
        <div className="flex justify-between items-center">
          <div className="flex justify-start items-center gap-5">
            <div className="self-stretch justify-start !text-[#2D3643] text-4xl font-medium ">
              Statement
            </div>
            <Select
              showSearch
              placeholder="Select a person"
              optionFilterProp="label"
              defaultValue="dealers"
              onChange={handleSelectChange}
              options={[
                { value: "dealers", label: "Dealers" },
                { value: "traders", label: "Traders" },
              ]}
            />
          </div>
          {/* <div className="px-4 py-2 bg-slate-100 rounded-md inline-flex justify-start items-center gap-7">
            <div className="justify-start">
              <span className="text-zinc-700 text-base font-normal ">
                Total Statement:
              </span>
              <span className="text-zinc-700 text-base font-medium "> 500</span>
            </div>
            <div className="w-3 h-0 rotate-90 outline outline-1 outline-offset-[-0.50px] outline-slate-600">
              |
            </div>
            <div className="justify-start">
              <span className="text-zinc-700 text-base font-normal ">
                Total Transaction:
              </span>
              <span className="text-zinc-700 text-base font-medium ">
                {" "}
                1,000
              </span>
            </div>
          </div> */}
        </div>

        <div className="flex justify-start mt-6 flex-wrap gap-2">
          <Dropdown
            overlay={DateTimeSelector}
            trigger={["click"]}
            open={openDropdown}
            onOpenChange={setOpenDropdown}
          >
            <IconButton
              className="flex items-center text-[#7a8799]"
              // nextIcon={<BottomDropdownIcon color="#7a8799" />}
            >
              Date & Time
            </IconButton>
          </Dropdown>

          <Select
            placeholder="Zone"
            // options={status}
            suffixIcon={<BottomDropdownIcon color="#7a8799" />}
            // onChange={(val) => handleFilter("spayStatus", val)}
            className="min-w-[100px]"
            allowClear
          />

          <Select
            placeholder="District"
            // options={status}
            suffixIcon={<BottomDropdownIcon color="#7a8799" />}
            // onChange={(val) => handleFilter("spayStatus", val)}
            className="min-w-[100px]"
            allowClear
          />

          <Select
            placeholder="Business Unit"
            // options={status}
            suffixIcon={<BottomDropdownIcon color="#7a8799" />}
            // onChange={(val) => handleFilter("spayStatus", val)}
            className="min-w-[100px]"
            allowClear
          />

          <Select
            placeholder="Sheba Payment Status"
            // options={status}
            suffixIcon={<BottomDropdownIcon color="#7a8799" />}
            // onChange={(val) => handleFilter("spayStatus", val)}
            className="min-w-[100px]"
            allowClear
          />

          <Select
            placeholder="Bank Status"
            suffixIcon={<BottomDropdownIcon color="#7a8799" />}
            value={filter?.mStatus ?? undefined}
            onChange={(value) => handleFilter("mStatus", value)}
            options={[
              { label: "Success", value: "success" },
              { label: "Pending", value: "pending" },
            ]}
            className="min-w-[100px]"
            allowClear
          />

          <Select
            placeholder="MR Status"
            suffixIcon={<BottomDropdownIcon color="#7a8799" />}
            value={filter?.omrStatus ?? undefined}
            onChange={(value) => handleFilter("omrStatus", value)}
            options={[
              { label: "Approved", value: "approved" },
              { label: "Pending", value: "pending" },
            ]}
            className="min-w-[100px]"
            allowClear
          />

          <Input
            placeholder="Search dealer’s name or code"
            className="text-[#AFBACA] !w-[280px]"
            prefix={<SearchIcon />}
            onChange={handleSearch}
            allowClear
          />
          {selectedCategory === "dealers" ? (
            <Button
              className="!text-primary bg-[#147AFF33]"
              loading={excelLoading}
              onClick={handleDownload}
            >
              Export
            </Button>
          ) : (
            <Button
              className="!text-primary bg-[#147AFF33]"
              loading={excelLoading}
              onClick={handleDownloadTraders}
            >
              Export
            </Button>
          )}
        </div>

        <div className="mt-4">
          {selectedCategory === "dealers" ? (
            <CustomTable
              columns={columns}
              className="text-header overflow-auto mt-4"
              loading={isLoading || isFetching}
              handleLimit={handleLimit}
              handlePagination={handlePagination}
              limit={limit}
              dataList={addKeyInArray(paymentHistoryData?.data.transactions)}
              page={page}
              bordered={false}
              total={paymentHistoryData?.data.metadata.total || 0}
              hideSpace
              paginationClassnames="dark:bg-primaryBackground bg-white dark:border-others-body border-others-borderLight py-4 px-4 relative"
            />
          ) : (
            <CustomTable
              columns={traderColumns}
              className="text-header overflow-auto mt-4"
              loading={traderGraphLoading || isFetching}
              handleLimit={handleLimit}
              handlePagination={handlePagination}
              limit={limit}
              dataList={addKeyInArray(treaderGraphData?.data?.rows)}
              page={page}
              bordered={false}
              total={treaderGraphData?.data?.count || 0}
              hideSpace
              paginationClassnames="dark:bg-primaryBackground bg-white dark:border-others-body border-others-borderLight py-4 px-4 relative"
            />
          )}
        </div>
      </div>
    </AppLayout>
  );
}

interface DataType {
  key: string;
  dealerCode: string;
  dealerName: string;
  date: string;
  time: string;
  businessUnit: string;
  amount: string;
  bankStatus: string;
  omrStatus: string;
}

const status = [
  {
    label: "Success",
    value: "success",
  },
  {
    label: "Pending",
    value: "failed",
  },
];
